import { ref, computed } from 'vue'
import { MONTHS_ID, DAY_NAMES, CMP_DEPT_TABS } from '../data/constants'
import { DEPT_TO_SEGMENT, REPORT_TO_SUBSEGMENT } from '../data/workflowMapping'

const compareOpen = ref(false)
const cmpDateLeft = ref('2026-02-17')
const cmpDateRight = ref('2026-02-10')
const cmpActiveDept = ref(0)
const cmpActiveReport = ref(0)
const cmpCompared = ref(false)
const isLoadingLeft = ref(false)
const isLoadingRight = ref(false)
const leftReportContent = ref('')
const rightReportContent = ref('')
const compareError = ref(null)

// Cache for fetched workflow data: "date::segment" → array of report rows
const compareCache = ref({})

const currentCmpDept = computed(() => CMP_DEPT_TABS[cmpActiveDept.value])
const currentCmpReport = computed(() => currentCmpDept.value.reports[cmpActiveReport.value])

function formatDateLabel(dateStr) {
  const d = new Date(dateStr)
  return `${DAY_NAMES[d.getDay()]}, ${d.getDate()} ${MONTHS_ID[d.getMonth()]} ${d.getFullYear()}`
}

const leftDateLabel = computed(() => formatDateLabel(cmpDateLeft.value))
const rightDateLabel = computed(() => formatDateLabel(cmpDateRight.value))

const diffDays = computed(() => {
  const dL = new Date(cmpDateLeft.value)
  const dR = new Date(cmpDateRight.value)
  return Math.round((dL - dR) / (1000 * 60 * 60 * 24))
})

const isLoading = computed(() => isLoadingLeft.value || isLoadingRight.value)

const leftReport = computed(() => leftReportContent.value)
const rightReport = computed(() => rightReportContent.value)

/**
 * Fetch workflow reports for a date+segment from API (with cache)
 */
async function fetchWorkflowReports(date, segment) {
  const key = `${date}::${segment}`
  if (compareCache.value[key]) return compareCache.value[key]

  const resp = await fetch(
    `/api/workflow/reports?date=${encodeURIComponent(date)}&segment=${encodeURIComponent(segment)}`
  )
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
  const data = await resp.json()
  const reports = data.reports || []
  compareCache.value[key] = reports
  return reports
}

/**
 * Extract report content from fetched workflow rows
 */
function extractContent(reports, reportId) {
  const subSegment = REPORT_TO_SUBSEGMENT[reportId]
  if (!subSegment) return null

  const row = reports.find(r => r.subSegment === subSegment)
  if (!row) return null

  return row.data || row.mdFile || null
}

/**
 * Run the comparison — fetches both dates from DB
 */
async function runCompare() {
  const dept = currentCmpDept.value
  const report = currentCmpReport.value
  const segment = DEPT_TO_SEGMENT[dept.id]

  compareError.value = null
  leftReportContent.value = ''
  rightReportContent.value = ''

  if (!segment) {
    compareError.value = `Belum ada data workflow untuk department "${dept.label}".`
    cmpCompared.value = true
    return
  }

  isLoadingLeft.value = true
  isLoadingRight.value = true
  cmpCompared.value = false

  try {
    // Fetch both dates in parallel
    const [leftRows, rightRows] = await Promise.all([
      fetchWorkflowReports(cmpDateLeft.value, segment),
      fetchWorkflowReports(cmpDateRight.value, segment),
    ])

    const leftContent = extractContent(leftRows, report.id)
    const rightContent = extractContent(rightRows, report.id)

    leftReportContent.value = leftContent || ''
    rightReportContent.value = rightContent || ''

    if (!leftContent && !rightContent) {
      compareError.value = `Tidak ada data "${report.label}" untuk kedua tanggal yang dipilih.`
    } else if (!leftContent) {
      compareError.value = `Tidak ada data Source (${cmpDateLeft.value}) untuk "${report.label}".`
    } else if (!rightContent) {
      compareError.value = `Tidak ada data Target (${cmpDateRight.value}) untuk "${report.label}".`
    }

    cmpCompared.value = true
  } catch (err) {
    compareError.value = `Gagal memuat data: ${err.message}`
    cmpCompared.value = true
  } finally {
    isLoadingLeft.value = false
    isLoadingRight.value = false
  }
}

const cmpSummaryHtml = computed(() => {
  const dept = currentCmpDept.value
  const report = currentCmpReport.value
  const absDiff = Math.abs(diffDays.value)
  const direction = diffDays.value > 0 ? 'setelah' : 'sebelum'

  let s = `🧠 <strong>AI Compare Summary</strong> — ${dept.icon} ${dept.label} / ${report.label}`
  s += `<br><span style="opacity:.6">Perbandingan ${absDiff} hari ${direction}</span>`

  if (compareError.value) {
    s += `<br><br>⚠️ ${compareError.value}`
  } else if (leftReportContent.value && rightReportContent.value) {
    s += `<br><br>✅ Data berhasil dimuat dari kedua tanggal. Silakan bandingkan konten di atas.`
  }

  return s
})

const cmpFooterText = computed(() => {
  if (!cmpCompared.value)
    return 'Pilih tanggal lalu klik Compare untuk melihat perbedaan'
  const dept = currentCmpDept.value
  return `✅ Compared: <strong>${dept.icon} ${dept.label}</strong> — <strong>${leftDateLabel.value}</strong> vs <strong>${rightDateLabel.value}</strong>`
})

function openCompare(currentDate) {
  cmpCompared.value = false
  cmpActiveDept.value = 0
  cmpActiveReport.value = 0
  leftReportContent.value = ''
  rightReportContent.value = ''
  compareError.value = null
  cmpDateLeft.value = currentDate
  const d = new Date(currentDate)
  d.setDate(d.getDate() - 7)
  cmpDateRight.value = d.toISOString().slice(0, 10)
  compareOpen.value = true
}

function closeCompare() {
  compareOpen.value = false
}

function switchCmpDept(index) {
  cmpActiveDept.value = index
  cmpActiveReport.value = 0
  cmpCompared.value = false
  leftReportContent.value = ''
  rightReportContent.value = ''
  compareError.value = null
}

function switchCmpReport(index) {
  cmpActiveReport.value = index
  cmpCompared.value = false
  leftReportContent.value = ''
  rightReportContent.value = ''
  compareError.value = null
}

function refreshCompare() {
  cmpCompared.value = false
  leftReportContent.value = ''
  rightReportContent.value = ''
  compareError.value = null
}

export function useCompare() {
  return {
    compareOpen,
    cmpDateLeft,
    cmpDateRight,
    cmpActiveDept,
    cmpActiveReport,
    cmpCompared,
    currentCmpDept,
    currentCmpReport,
    leftDateLabel,
    rightDateLabel,
    leftReport,
    rightReport,
    isLoading,
    isLoadingLeft,
    isLoadingRight,
    compareError,
    cmpSummaryHtml,
    cmpFooterText,
    diffDays,
    openCompare,
    closeCompare,
    switchCmpDept,
    switchCmpReport,
    runCompare,
    refreshCompare,
  }
}
